<template>
  <div v-show="show">
    <b-card title="Notícia" header-tag="header" footer-tag="footer">
      <div slot="header" class="row mb-0 align-items-right">
        <div class="col">
          <div class="switch-container">
            <toggle-button class="mb-0"
              v-model="form.highlight"
              :width="70"
              :height="25"
              :color="'#d39e00'"
              switch-color="#d39e00"
              :labels="{ checked: 'SIM', unchecked: 'NÃO' }"/>

            <span class="switch-label ml-1">
              Destaque
            </span>
          </div>
        </div>
        <div class="col text-right">
          <div class="switch-container">
            <span class="switch-label mr-1">
              Status
            </span>

            <toggle-button class="mb-0"
              v-model="form.status"
              :width="70"
              :height="25"
              :labels="{checked: 'Ativo', unchecked: 'Inativo'}"/>
          </div>
        </div>
      </div>

      <div class="row">
        <b-form-group class="col-12 col-md-4"
          label="Imagem"
          label-for="media">

          <single-media-uploader
            name="file"
            @success="handleUploadMedia"
            :url="mediaUrl"
            placeholder="static/img/defaults/upload.png"
            :source="mediaInstance">
          </single-media-uploader>
        </b-form-group>

        <div class="col-12 col-md-8">
          <div class="row">
            <b-form-group class="col-12"
              label="Título"
              label-for="title"
              description="(Obrigatório)">

              <b-form-input id="title"
                v-model="form.title"
                placeholder="Titulo"
                required
                :class="{
                  'is-invalid': submitted && form.errors.has('title'),
                  'is-valid': submitted && !form.errors.has('title')
                }"
                @input="slugify">
              </b-form-input>
            </b-form-group>

            <b-form-group class="col-12"
              label="Slug"
              label-for="slug"
              description="(Se vazia será gerada uma slug automática)">

              <b-form-input id="slug"
                v-model="form.slug"
                placeholder="Slug"
                :class="{
                  'is-invalid': submitted && form.errors.has('slug'),
                  'is-valid': submitted && !form.errors.has('slug')
                }"
                @change="userEditedSlug = true">
              </b-form-input>
            </b-form-group>

            <b-form-group class="col-12"
              label="Manchete"
              label-for="headline">

              <b-form-input id="headline"
                v-model="form.headline"
                placeholder="Manchete"
                :class="{
                  'is-invalid': submitted && form.errors.has('headline'),
                  'is-valid': submitted && !form.errors.has('headline')
                }">
              </b-form-input>
            </b-form-group>
          </div>
        </div>
      </div>

      <div class="row">
        <b-form-group class="col-12 col-md-4"
          label="Data de publicação"
          label-for="release_date">

          <date-picker v-model="form.release_date"
            :config="datepickerOptions"
            @dp-change="updateReleaseDate"
            :class="{
              'is-invalid': submitted && form.errors.has('release_date'),
              'is-valid': submitted && !form.errors.has('release_date')
            }"/>
        </b-form-group>

        <b-form-group class="col-12 col-md-4"
          label="Categoria"
          label-for="category_id">

          <multiselect v-model="selectedCategory"
            @select="updateForm('category_id', arguments[0])"
            :allowEmpty="true"
            :options="categories"
            label="name"
            placeholder="Selecione a categoria"
            :showLabels="false"
            track-by="id"
            :class="{
              'is-invalid': submitted && form.errors.has('category_id'),
              'is-valid': submitted && !form.errors.has('category_id')
            }"/>
        </b-form-group>

        <b-form-group class="col-12 col-md-4"
          label="Processo"
          label-for="process_id">

          <multiselect v-model="selectedProcess"
            @select="updateForm('process_id', arguments[0])"
            placeholder="Selecione o processo"
            :allowEmpty="true"
            :options="processes"
            label="company_name"
            :showLabels="false"
            track-by="id"
            :class="{
              'is-invalid': submitted && form.errors.has('process_id'),
              'is-valid': submitted && !form.errors.has('process_id')
            }"/>
        </b-form-group>
      </div>

      <div class="row">
        <b-form-group class="col-12"
          label="Corpo"
          label-for="body">

          <rich-textarea v-model="form.body"></rich-textarea>
        </b-form-group>
      </div>
    </b-card>

    <b-card title="SEO / URLs amigáveis" header-tag="header" footer-tag="footer">
      <seo-fields :form="form" :urls="friendlyUrls" :slugable="form.title"/>

      <slot name="footer"></slot>
    </b-card>
  </div>
</template>

<script>
import slugify from 'slugify'
import { isEmpty, get, set } from 'lodash'
import { resolveHTTP } from '@/services/api'
import { datepickerOptions, slugifyOptions } from '@/plugins'

export default {
  props: {
    form: {
      type: [Object],
      required: true
    },

    submitted: {
      type: Boolean,
      default: false
    },

    instance: {
      type: Object,
      default () {
        return {
          media: [],
          friendlyUrls: []
        }
      }
    },

    http: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      show: true,
      userEditedSlug: false,

      locks: {
        processes: true,
        categories: true
      },

      media: {},

      datepickerOptions: Object.assign(datepickerOptions),

      selectedCategory: null,
      categories: [],

      selectedProcess: null,
      processes: [],

      empty: {
        slug: '',
        title: '',
        headline: '',
        body: '',
        release_date: '',
        process_id: null,
        category_id: null,
        media_ids: [],
        highlight: false,
        status: true
      }
    }
  },

  watch: {
    '$props': {
      handler: function (newProps) {
        if (!get(newProps, 'form.process_id', false)) {
          this.selectedProcess = null
        }

        if (!get(newProps, 'form.category_id', false)) {
          this.selectedCategory = null
        }
      },
      deep: true
    }
  },

  computed: {
    ready () {
      return !(this.locks.categories || this.locks.processes)
    },

    friendlyUrls () {
      return this.instance ? get(this.instance, 'friendly_urls', []) : []
    },

    mediaUrl () {
      return this.http.routes.uploadMedia(this.instance.id)
    },

    mediaInstance () {
      if (!isEmpty(this.media)) {
        return this.media
      }

      if (!isEmpty(this.instance.media)) {
        return this.instance.media[0]
      }

      return {}
    }
  },

  methods: {
    updateForm (path, value) {
      set(this.form, path, value ? value.id : null)
    },

    updateReleaseDate ({ date, oldDate }) {
      this.form.release_date = date ? date.format('YYYY-MM-DD HH:mm:ss') : null
    },

    handleUploadMedia (file, response, progress) {
      // 1. we will set the new media id on the form for latter process
      this.form.media_ids = [response.id]

      // 2. Let's set the media on the source for the upload field
      this.media = response
    },

    slugify () {
      const self = this

      if (this.userEditedSlug) {
        return
      }

      self.show = false
      self.form.slug = slugify(self.form.title, slugifyOptions)
      self.$nextTick(() => {
        self.show = true
      })
    }
  },

  created () {
    const self = this
    // Lets grab the data for the form
    resolveHTTP('/bl/story-categories').index({ params: { _onepage: true } })
      .then(function (res) {
        self.categories = res.data.collection
        self.locks.categories = false

        self.selectedCategory = self.form.category_id
          ? self.categories.find((o) => o.id === self.form.category_id)
          : null
      })

    resolveHTTP('/bl/processes').index({ params: { _onepage: true } })
      .then(function (res) {
        self.processes = res.data.collection
        self.locks.processes = false

        self.selectedProcess = self.form.process_id
          ? self.processes.find((o) => o.id === self.form.process_id)
          : null
      })

    if (self.instance.id) {
      self.form.setInitialValues(self.instance)
      self.form.category_id = self.instance.category_id || null
      self.form.process_id = self.instance.process_id || null
      self.releaseDate = self.instance.release_date || null
    } else {
      self.form.withData(self.empty).setInitialValues(self.empty)
    }
  }
}
</script>

<style lang="sass" scoped>
  .highlight-checkbox
    cursor: pointer
    font-size: 18px

    i
      color: #444

      &.highlighted
        color: #fdd049

</style>



// WEBPACK FOOTER //
// src/components/forms/Bl/Story.vue